import { Color3, DynamicTexture, Mesh, MeshBuilder, StandardMaterial, TransformNode, Vector3 } from "@babylonjs/core";
import { CART } from "@rivalwatch/shared";
import { glow } from "../../game/materials.ts";
import { inkEdges, NEUTRAL, payloadPart, pushRing } from "../default.ts";
import type { PayloadContext, PayloadView, TrackContext } from "../types.ts";

const GREEN = "#2f6b58";
const CREAM = "#f1e6c8";
const BRASS = "#d9a93a";
const IRON = "#2c3431";
const TROLLEY_TOP = 5.55; // meets the overhead wire (tools/map/brussels.py, WIRE_Z)

/**
 * The payload: a vintage tram flatcar in bottle green and cream, its trolley pole up on the
 * overhead wire, carrying a strapped stack of giant comic albums (the straps take the state
 * colour). A speech balloon floats over it instead of an orb. Collision is CART's two boxes.
 */
export function tramPayload(ctx: PayloadContext): PayloadView {
  const { scene } = ctx;
  const root = ctx.own(new TransformNode("payload", scene));
  const part = (m: Mesh, x: number, y: number, z: number, hex: string, edges = true) => payloadPart(ctx, root, m, x, y, z, hex, edges, false);
  part(MeshBuilder.CreateBox("tramFrame", { width: 2.1, height: 0.34, depth: 3.3 }, scene), 0, 0.45, 0, GREEN);
  part(MeshBuilder.CreateBox("tramBand", { width: 2.16, height: 0.1, depth: 3.36 }, scene), 0, 0.5, 0, CREAM, false);
  part(MeshBuilder.CreateBox("tramDeck", { width: CART.hw * 2, height: 0.36, depth: CART.hl * 2 }, scene), 0, CART.deck - 0.18, 0, "#e3d6b8");
  const wheels: Mesh[] = [];
  for (const x of [-1.02, 1.02])
    for (const z of [-1.2, 1.2]) {
      const w = part(MeshBuilder.CreateCylinder("tramWheel", { diameter: 0.66, height: 0.2, tessellation: 16 }, scene), x, 0.33, z, "#1f2229", false);
      w.rotation.z = Math.PI / 2;
      const hub = part(MeshBuilder.CreateCylinder("tramHub", { diameter: 0.26, height: 0.24, tessellation: 10 }, scene), x, 0.33, z, BRASS, false);
      hub.rotation.z = Math.PI / 2;
      wheels.push(w, hub);
    }
  for (const z of [-CART.hl - 0.12, CART.hl + 0.12]) {
    part(MeshBuilder.CreateBox("tramBeam", { width: 2.0, height: 0.26, depth: 0.14 }, scene), 0, 0.55, Math.sign(z) * (CART.hl - 0.05), IRON);
    for (const x of [-0.72, 0.72]) {
      const b = part(MeshBuilder.CreateCylinder("tramBuffer", { diameter: 0.3, height: 0.26, tessellation: 12 }, scene), x, 0.55, z, BRASS, false);
      b.rotation.x = Math.PI / 2;
    }
  }
  const lamp = part(MeshBuilder.CreateCylinder("tramLamp", { diameter: 0.26, height: 0.16, tessellation: 12 }, scene), 0, 0.82, CART.hl + 0.02, BRASS, false);
  lamp.rotation.x = Math.PI / 2;
  // The trolley pole, from the deck's back up to the wire, and its little wheel.
  const base = new Vector3(0, CART.deck, -1.45);
  const top = new Vector3(0, TROLLEY_TOP, 0.4);
  const len = Vector3.Distance(base, top);
  const pole = part(MeshBuilder.CreateCylinder("tramPole", { diameter: 0.07, height: len, tessellation: 6 }, scene), 0, (base.y + top.y) / 2, (base.z + top.z) / 2, IRON, false);
  pole.rotation.x = Math.atan2(top.z - base.z, top.y - base.y);
  const shoe = part(MeshBuilder.CreateCylinder("tramShoe", { diameter: 0.22, height: 0.06, tessellation: 12 }, scene), 0, TROLLEY_TOP - 0.05, 0.4, BRASS, false);
  shoe.rotation.z = Math.PI / 2;
  part(MeshBuilder.CreateBox("tramPoleBase", { width: 0.4, height: 0.25, depth: 0.4 }, scene), 0, CART.deck + 0.12, -1.45, IRON);

  // The cargo: three giant albums, each cover-boarded with cream pages, the top one titled.
  const covers = ["#d8a65a", "#3f7f66", "#efe4c6"];
  const skew = [0.03, -0.05, 0.02];
  const t = (CART.coreH - 0.02) / 3;
  for (let k = 0; k < 3; k++) {
    const y = CART.deck + t * (k + 0.5);
    const book = new TransformNode(`album${k}`, scene);
    book.parent = root;
    book.position.set(0, y, 0);
    book.rotation.y = skew[k]!;
    const sub = (m: Mesh, x: number, yy: number, z: number, hex: string, edges = true) => payloadPart(ctx, book, m, x, yy, z, hex, edges, false);
    sub(MeshBuilder.CreateBox("albumPages", { width: CART.coreHw * 2 - 0.08, height: t * 0.7, depth: CART.coreHl * 2 - 0.06 }, scene), 0.02, 0, 0, CREAM, false);
    for (const yy of [-t * 0.42, t * 0.42]) sub(MeshBuilder.CreateBox("albumBoard", { width: CART.coreHw * 2, height: t * 0.16, depth: CART.coreHl * 2 }, scene), 0, yy, 0, covers[k]!);
    sub(MeshBuilder.CreateBox("albumSpine", { width: 0.08, height: t, depth: CART.coreHl * 2 }, scene), -CART.coreHw + 0.04, 0, 0, covers[k]!);
    if (k === 2) {
      const title = MeshBuilder.CreatePlane("albumTitle", { width: CART.coreHw * 2 - 0.12, height: CART.coreHl * 2 - 0.12 }, scene);
      title.parent = book;
      title.position.set(0, t * 0.5 + 0.011, 0);
      title.rotation.x = Math.PI / 2;
      title.rotation.y = Math.PI;
      title.material = ctx.own(albumCover(ctx));
      title.isPickable = false;
      title.receiveShadows = true;
    }
  }
  const glows: Mesh[] = [];
  const lit = (m: Mesh, x: number, y: number, z: number) => {
    m.parent = root;
    m.position.set(x, y, z);
    m.material = glow(scene, NEUTRAL);
    m.isPickable = false;
    glows.push(m);
    return m;
  };
  // Straps round the stack, and a lit edge along each side of the deck.
  for (const z of [-0.52, 0.52]) lit(MeshBuilder.CreateBox("albumStrap", { width: CART.coreHw * 2 + 0.07, height: CART.coreH + 0.05, depth: 0.12 }, scene), 0, CART.deck + CART.coreH / 2, z);
  for (const x of [-CART.hw, CART.hw]) lit(MeshBuilder.CreateBox("tramStrip", { width: 0.07, height: 0.07, depth: CART.hl * 2 }, scene), x, CART.deck, 0);
  // The speech balloon over it all.
  const disc = MeshBuilder.CreateDisc("balloonDisc", { radius: 0.5, tessellation: 28, sideOrientation: Mesh.DOUBLESIDE }, scene);
  disc.scaling.set(1.3, 0.92, 1);
  disc.bakeCurrentTransformIntoVertices();
  const tail = MeshBuilder.CreateDisc("balloonTail", { radius: 0.22, tessellation: 3, sideOrientation: Mesh.DOUBLESIDE }, scene);
  tail.rotation.z = -Math.PI / 2 - 0.35;
  tail.position.set(0.12, -0.46, 0);
  tail.bakeCurrentTransformIntoVertices();
  const orb = Mesh.MergeMeshes([disc, tail], true)!;
  orb.name = "payloadBalloon";
  orb.billboardMode = TransformNode.BILLBOARDMODE_Y;
  inkEdges(orb, ctx.ink, 5);
  const orbHeight = CART.deck + CART.coreH + 0.95;
  lit(orb, 0, orbHeight, 0);
  let last = 0;
  return {
    root,
    glows,
    ring: pushRing(ctx, root),
    orb,
    orbHeight,
    update(now, moving) {
      const dt = Math.min(0.1, last ? now - last : 0);
      last = now;
      // A balloon faces you; it doesn't spin like an orb.
      orb.rotation.y = 0;
      if (moving) for (const w of wheels) w.rotation.x += dt * 3.4;
    },
  };
}

/** The top album's cover: a title in comic lettering over a moon and a tram. */
function albumCover(ctx: PayloadContext) {
  const tex = new DynamicTexture("albumCover", { width: 512, height: 600 }, ctx.scene, true);
  const g = tex.getContext() as unknown as CanvasRenderingContext2D;
  const draw = () => {
    g.fillStyle = "#efe4c6";
    g.fillRect(0, 0, 512, 600);
    g.fillStyle = "#7fc4cf";
    g.fillRect(24, 150, 464, 420);
    g.fillStyle = "#f0c84a";
    g.beginPath();
    g.arc(340, 300, 96, 0, Math.PI * 2);
    g.fill();
    g.fillStyle = GREEN;
    g.fillRect(90, 440, 250, 70);
    g.fillStyle = CREAM;
    g.fillRect(90, 462, 250, 12);
    g.fillStyle = "#1f2229";
    for (const x of [130, 300]) {
      g.beginPath();
      g.arc(x, 518, 18, 0, Math.PI * 2);
      g.fill();
    }
    g.strokeStyle = "#1a1c24";
    g.lineWidth = 6;
    g.beginPath();
    g.moveTo(170, 440);
    g.lineTo(250, 330);
    g.stroke();
    g.lineWidth = 10;
    g.strokeRect(24, 150, 464, 420);
    g.fillStyle = "#1a1c24";
    g.textAlign = "center";
    g.font = "700 64px 'Comic Neue', 'Comic Sans MS', sans-serif";
    g.fillText("OBJECTIF", 256, 72);
    g.fillText("PALAIS", 256, 132);
    tex.update(false);
  };
  draw();
  document.fonts.load("700 64px 'Comic Neue'").then(draw, () => undefined);
  const mat = new StandardMaterial("albumCover", ctx.scene);
  mat.diffuseTexture = tex;
  mat.specularColor = Color3.Black();
  mat.onDisposeObservable.addOnce(() => tex.dispose());
  return mat;
}

/** The route: grooved tram rails set flush in a band of granite setts, kerbed in pale stone. */
export function tramTrack({ scene, track, ink, own }: TrackContext) {
  const pts = track.points;
  const side = (i: number, off: number, y: number) => {
    const p = pts[i]!;
    const tg = track.tangents[i]!;
    return new Vector3(p.x - tg.z * off, p.y + y, p.z + tg.x * off);
  };
  const strip = (name: string, a: number, b: number, y: number, hex: string, edges: boolean) => {
    const m = own(MeshBuilder.CreateRibbon(name, { pathArray: [pts.map((_, i) => side(i, a, y)), pts.map((_, i) => side(i, b, y))], sideOrientation: 2 }, scene));
    const mat = own(new StandardMaterial(`${name}-mat`, scene));
    mat.diffuseColor = Color3.FromHexString(hex);
    mat.specularColor = Color3.Black();
    mat.zOffset = -2;
    mat.backFaceCulling = false;
    m.material = mat;
    m.receiveShadows = true;
    m.isPickable = false;
    if (edges) inkEdges(m, ink, 3);
    return m;
  };
  strip("setts", -1.3, 1.3, 0.05, "#8b857b", true);
  for (const s of [-1, 1]) {
    strip("kerb", s * 1.3, s * 1.45, 0.055, "#cfc6b2", false);
    strip("rail", s * 0.68, s * 0.76, 0.06, "#c3c9cf", false);
    strip("groove", s * 0.62, s * 0.68, 0.058, "#2c3431", false);
  }
}
